<script async setup lang="ts">
import { ParticipantEntity } from '@/entities/interfaces/participant';
import { useEventStore } from '@/stores/events.store';
import { useModalStore } from '@/stores/signup.store';
import { FormField, Input, Checkbox, Button } from '@albank/vue-ui-kit';
import { addParticipant } from '@/entities/api/participant';
import { ref, watch } from 'vue';
type fields =
  | 'fio'
  | 'phone'
  | 'email'
  | 'occupation'
  | 'employment'
  | 'consent';

const eventStore = useEventStore();
const event = eventStore.getEvent;

const modalStore = useModalStore();
const modal = modalStore.getModal;

const form = ref<HTMLFormElement>();
const phone = ref<string>('+7 ');
const response = ref<number>();

const filterPhone = (phone: string) => {
  return Number(phone.replaceAll(' ', '').replaceAll('+', ''));
};

const inputPhone = (e: InputEvent) => {
  const item = e.target as HTMLInputElement;
  const phoneValue = filterPhone(item.value);

  if (item.value.length < 3 || item.value.length > 16 || isNaN(phoneValue)) {
    item.value = phone.value;
    return;
  }

  if (phone.value.length > item.value.length) {
    phone.value = item.value;
    return;
  }

  phone.value = item.value;
  const length = phone.value.length;

  if ([6, 10, 13].includes(length)) {
    phone.value = phone.value + ' ';
  }
};

const validate = (value: string, field: fields) => {
  let isValid = false;

  if (field == 'fio') {
    isValid = value.length > 5;
  } else if (field == 'consent') {
    isValid = value != 'false';
  } else if (field == 'phone') {
    isValid = value.length == 16;
  } else if (field == 'email') {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    isValid = regex.test(value);
  } else {
    isValid = true;
  }

  fieldsErrorStatus.value[field] = !isValid;
  return isValid;
};

const confirmForm = (e?: Event) => {
  e?.preventDefault();
  if (!form.value) return;

  let isFine = true;
  const items = form.value.querySelectorAll('input');

  const fields: ParticipantEntity = {
    fio: '',
    phone: '',
    email: '',
    occupation: '',
    employment: '',
    consent: '',
    eventId: event.value.id
  };

  items.forEach((item) => {
    const value = item.name != 'consent' ? item.value : item.checked;
    const isValid = validate(String(value), item.name as fields);

    if (isValid) {
      if (item.name == 'consent') {
        fields['consent'] = value ? 'true' : 'false';
      } else if (item.name == 'phone') {
        fields['phone'] = String(filterPhone(item.value));
      } else if (
        ['fio', 'email', 'occupation', 'employment'].includes(item.name)
      ) {
        fields[item.name as 'fio' | 'email' | 'occupation' | 'employment'] =
          item.value;
      }
    } else {
      isFine = false;
    }
  });

  if (!isFine) return;

  addParticipant(fields as ParticipantEntity).then((res) => {
    if (res.data.addParticipant) {
      response.value = 200;
    }
  });
};

const fieldsErrorStatus = ref({
  fio: false,
  phone: false,
  email: false,
  occupation: false,
  employment: false,
  consent: false
});

const errors = {
  fio: 'Обязательное поле',
  phone: 'Неверный формат',
  email: 'Неверный формат'
};

watch(modal, () => {
  if (modal.value) {
    document.body.style.overflowY = 'hidden';
  } else {
    document.body.style.overflowY = 'auto';
  }
});
</script>

<template>
  <div v-if="modal" class="wrapper">
    <section class="signup" v-if="!response">
      <div class="mobile-header">
        <img src="/assets/icons/logo.svg" alt="Логотип АЭБ" class="logo" />
        <img
          @click="modalStore.toggleModal()"
          src="/assets/icons/close.svg"
          alt="Закрыть"
        />
      </div>
      <form @submit="confirmForm" ref="form">
        <div class="close" @click="modalStore.toggleModal()">
          <img src="/assets/icons/close.svg" alt="Закрыть" />
        </div>
        <h2>Регистрация на мероприятие</h2>
        <FormField :error="fieldsErrorStatus.fio" errorMessage="Meow">
          <template #label>ФИО*</template>
          <Input
            type="text"
            @blur="(e) => validate((e.target as HTMLInputElement).value, 'fio')"
            name="fio"
          />
          <label v-if="fieldsErrorStatus.fio" class="error">{{
            errors.fio
          }}</label>
        </FormField>
        <FormField :error="fieldsErrorStatus.phone">
          <template #label>Контактный телефон*</template>
          <Input
            name="phone"
            type="tel"
            @input="(e) => inputPhone(e)"
            :value="phone"
            @blur="
              (e) => validate((e.target as HTMLInputElement).value, 'phone')
            "
          />

          <label v-if="fieldsErrorStatus.phone" class="error">{{
            errors.phone
          }}</label>
          <label v-else
            >Для подтверждения присутствия ближе к дате мероприятия</label
          >
        </FormField>
        <FormField :error="fieldsErrorStatus.email">
          <template #label>Электронная почта</template>
          <Input
            type="text"
            name="email"
            @blur="
              (e) => validate((e.target as HTMLInputElement).value, 'email')
            "
          />
          <label v-if="fieldsErrorStatus.email" class="error">{{
            errors.email
          }}</label>
        </FormField>
        <FormField :error="fieldsErrorStatus.occupation">
          <template #label>Вид деятельности</template>
          <Input type="text" name="occupation" />
        </FormField>
        <FormField :error="fieldsErrorStatus.employment">
          <template #label>Должность и место работы</template>
          <Input type="text" name="employment" />
        </FormField>
        <Checkbox :hasError="fieldsErrorStatus.consent" name="consent">
          <p>
            Даю согласие на
            <a
              href="https://www.albank.ru/ru/more/info/personal-policy/"
              target="_blank"
              >обработку персональных данных</a
            >
          </p>
        </Checkbox>
        <Button variant="primary" class="confirm" type="submit">
          Отправить
        </Button>
      </form>
    </section>
    <div v-else class="response">
      <div class="close" @click="modalStore.toggleModal()">
        <img src="/assets/icons/close.svg" alt="Закрыть" />
      </div>
      <div class="text-content">
        <h2>Вы успешно зарегистрированы</h2>
        <p>
          Будьте в курсе всего самого важного — подписывайтесь на наш
          Telegram-канал
        </p>
      </div>
      <img src="/assets/icons/telegram-chanell.png" alt="" />
      <a class="link-button" href="https://t.me/aeb_course">Подписаться</a>
      <button class="close-btn" @click="modalStore.toggleModal()">
        Закрыть
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  top: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  position: fixed;

  display: flex;
  align-items: center;
  justify-content: center;
  background: #27303e4d;

  @media screen and (max-width: 1024px) {
    position: fixed;
    height: calc(100vh);
  }
}

.signup {
  position: relative;

  @media screen and (max-width: 1024px) {
    width: 100%;
    height: 100%;
  }

  label {
    top: 64px;
    position: absolute;
    color: var(--color-mono-400);
    font-size: var(--font-size-xxs);

    @media screen and (max-height: 760px) {
      top: 56px;
    }
  }

  .error {
    color: var(--color-red-500);
  }

  a {
    color: var(--color-blue-500);
  }

  h2 {
    padding-bottom: 16px;
    font-size: var(--font-size-lm);
  }

  form {
    gap: 32px;
    width: 680px;
    display: flex;
    padding: 64px 60px;
    border-radius: 32px;
    flex-direction: column;
    background: var(--color-mono-0);

    @media screen and (max-width: 1600px) {
      gap: 28px;
      padding: 48px 60px;
    }

    @media screen and (max-height: 760px) {
      gap: 16px;
      padding: 36px 60px;
    }

    @media screen and (max-width: 1024px) {
      top: 86px;
      left: 0;
      gap: 36px;
      width: 100%;
      height: calc(100% - 88px);
      padding: 18px 16px 80px;
      border-radius: 0;
      position: absolute;
      overflow-y: scroll;
    }

    .aeb-form-field {
      margin: 8px 0 0;
    }
  }

  .confirm {
    font-size: 16px !important;
  }
}

.mobile-header {
  height: 88px;
  display: none;
  padding: 0 16px;
  align-items: center;
  justify-content: space-between;
  background: var(--color-mono-0);

  @media screen and (max-width: 1024px) {
    display: flex;
  }
}

.close {
  width: 48px;
  height: 48px;
  padding: 8px;
  border-radius: 50%;
  background: var(--color-mono-0);

  top: 0;
  right: 0;
  cursor: pointer;
  position: absolute;
  transform: translate(48px, -48px);

  @media screen and (max-width: 1600px) {
    transform: translate(56px, 0);
  }

  img {
    width: 32px;
  }
}

.response {
  gap: 24px;
  display: flex;
  padding: 40px;
  max-width: 520px;
  border-radius: 32px;
  flex-direction: column;
  position: relative;
  background: var(--color-mono-0);

  .close-btn {
    display: none;
  }

  .text-content {
    gap: 16px;
    display: flex;
    flex-direction: column;

    h2 {
      font-size: var(--font-size-lm);
      line-height: var(--line-height-medium);
    }
  }

  @media screen and (max-width: 1024px) {
    max-width: calc(100vw - 32px);

    .close-btn {
      display: block;
      border: none;
      padding: 12px !important;
      text-align: center !important;
    }

    .close {
      display: none;
    }
  }
}
</style>
